import { Spinner } from '@assembly-js/design-system';
import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';

import { APP_BUILDER_BUILD_TAB_PATH } from 'src/constants';
import { RouteContext, useFeatureFlags } from 'src/context';
import useNavigate from 'src/hooks/useNavigate';
import { useAppSelector } from 'src/hooks/useStore';

import { isOpenAddAppMessage, replyAddAppReady } from './internalPortalTab';

const [ADD_APP_PATHNAME] = APP_BUILDER_BUILD_TAB_PATH.split('?');
const CONTENT_PANEL_ID = 'primary-content-panel';
// Above the page and its drawers, level with MUI's modal layer.
const COVER_Z_INDEX = 1300;

interface CoverBounds {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Starts downloading Add App's code while the owner is in the client preview, so
 * the switch to it is quicker once they come back.
 */
export const preloadAddAppPage = () => {
  // A failed preload only means the navigation fetches the code itself.
  import(
    /* webpackChunkName: 'app-builder' */ 'src/routes/appSetup/builder'
  ).catch(() => undefined);
};

// The page content beside the sidebar, or the whole window in layouts without
// a content panel.
const getCoverBounds = (): CoverBounds => {
  const panel = document.getElementById(CONTENT_PANEL_ID);
  if (!panel) {
    return {
      top: 0,
      left: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }
  const { top, left, width, height } = panel.getBoundingClientRect();
  return { top, left, width, height };
};

/**
 * Opens Add App when the client preview this tab launched asks for it (see
 * requestAddAppInInternalPortalTab), navigating in-app like the sidebar's Add App.
 */
export const OpenAddAppFromClientPreview = () => {
  const { EnableOnboardingActivation } = useFeatureFlags();
  const isClient = useAppSelector((state) => state.user.isClient);
  const { pathname } = useContext(RouteContext);
  const { navigate } = useNavigate();
  // navigate is rebuilt every render; read the latest without re-subscribing.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [coverBounds, setCoverBounds] = useState<CoverBounds | null>(null);

  useEffect(() => {
    if (isClient || !EnableOnboardingActivation) {
      return undefined;
    }
    // No origin check: the preview runs on the portal's own domain, which can be
    // any custom domain, and the message only opens a fixed in-app page.
    const handleMessage = (event: MessageEvent<unknown>) => {
      if (!isOpenAddAppMessage(event.data)) {
        return;
      }
      // Rendering Add App blocks the tab for up to a few seconds, long enough
      // that it would come forward still showing the page underneath. Cover that
      // page first, let the preview switch here, and navigate only once the cover
      // is on screen.
      flushSync(() => setCoverBounds(getCoverBounds()));
      replyAddAppReady(event.source, event.origin);
      // Animation frames don't run in a hidden tab, so this waits for the switch.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          navigateRef.current(APP_BUILDER_BUILD_TAB_PATH);
          // An unsaved-changes prompt can hold the navigation; don't hide it.
          if (!window.location.pathname.endsWith(ADD_APP_PATHNAME)) {
            setCoverBounds(null);
          }
        }),
      );
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isClient, EnableOnboardingActivation]);

  const isOnAddApp = pathname === ADD_APP_PATHNAME;
  useEffect(() => {
    if (isOnAddApp) {
      setCoverBounds(null);
    }
  }, [isOnAddApp, coverBounds]);

  if (!coverBounds) {
    return null;
  }
  return createPortal(
    <div
      aria-busy="true"
      className="fixed flex items-center justify-center bg-white"
      style={{ ...coverBounds, zIndex: COVER_Z_INDEX }}
    >
      <Spinner size={10} />
    </div>,
    document.body,
  );
};
